import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  Link2, Users as UsersIcon, Activity as ActivityIcon, Eye, Copy,
  XCircle, ExternalLink, Lock, RefreshCcw, Calendar, AlertTriangle,
} from "lucide-react";
import {
  drStats, drAllMagicLinks, drInvestorsSummary, drAccessLog,
  drDeactivateMagicLink,
} from "../lib/api";
import { fmtDate } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";
import InvestorDrawer from "../components/investors/InvestorDrawer";

const TABS = [
  { id: "links",    label: "Magic-Links",         icon: Link2 },
  { id: "invs",     label: "Investoren-Übersicht", icon: UsersIcon },
  { id: "log",      label: "Zugriffs-Log",         icon: ActivityIcon },
];

const fmtRel = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "gerade eben";
    if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
    if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std`;
    if (diff < 86400 * 30) return `vor ${Math.floor(diff / 86400)} Tagen`;
    return d.toLocaleDateString("de-DE");
  } catch { return iso; }
};

export default function Datenraum() {
  const [tab, setTab] = useState("links");
  const [stats, setStats] = useState(null);
  const [magicLinks, setMagicLinks] = useState([]);
  const [investorsSummary, setInvestorsSummary] = useState([]);
  const [accessLog, setAccessLog] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [drawerId, setDrawerId] = useState(null);
  const [logFilter, setLogFilter] = useState("");      // "" | "team" | "investor"
  const [onlyAktiv, setOnlyAktiv] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, ml, inv, lg] = await Promise.all([
        drStats(),
        drAllMagicLinks(onlyAktiv),
        drInvestorsSummary(),
        drAccessLog({ akteur_typ: logFilter || undefined, page_size: 100 }),
      ]);
      setStats(s);
      setMagicLinks(ml.items || []);
      setInvestorsSummary(inv.items || []);
      setAccessLog(lg);
    } catch {
      toast.error("Datenraum-Übersicht konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [logFilter, onlyAktiv]);

  useEffect(() => { load(); }, [load]);

  const copyLink = (token) => {
    const url = `${window.location.origin}/datenraum/${token}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Magic-Link in Zwischenablage kopiert"),
      () => toast.error("Konnte nicht kopieren"),
    );
  };

  const handleDeactivate = async (token, firma) => {
    if (!window.confirm(`Magic-Link für „${firma || "?"}" wirklich deaktivieren? Der Investor verliert sofort den Zugriff.`)) return;
    try {
      await drDeactivateMagicLink(token);
      toast.success("Magic-Link deaktiviert");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deaktivieren fehlgeschlagen");
    }
  };

  const activeMlCount = useMemo(
    () => magicLinks.filter((m) => m.status === "aktiv").length,
    [magicLinks],
  );
  const investorsWithAccess = investorsSummary.filter((i) => i.magic_links_aktiv > 0).length;

  return (
    <div data-testid="datenraum-page" className="pb-container px-0 sm:px-4 lg:px-8 py-4 sm:py-8 space-y-6">
      <PageHeader
        eyebrow="Newton Campus · Investor-Verteilung"
        title="Datenraum"
        subtitle="Investor-Freigaben + Magic-Links + Zugriffs-Statistiken. Datei-Upload und Verwaltung erfolgt auf /dokumente."
        testid="datenraum-header"
        actions={
          <button
            data-testid="dr-refresh-btn"
            onClick={load}
            className="pb-btn-outline text-xs uppercase tracking-widish font-semibold min-h-[40px]"
          >
            <RefreshCcw size={13} /> Aktualisieren
          </button>
        }
      />

      {/* Stats-Strip */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dr-stats">
        <div className="pb-kpi">
          <div className="pb-eyebrow"><Link2 size={11} className="inline mr-1"/> Magic-Links aktiv</div>
          <div className="text-2xl font-display font-bold text-navy mt-1 tabular-nums">
            {activeMlCount} <span className="text-base text-navy/45">/ {magicLinks.length}</span>
          </div>
          <div className="text-[10px] text-navy/55 mt-1 font-sans">
            {stats?.magic_links_aktiv ?? "—"} laut Backend
          </div>
        </div>
        <div className="pb-kpi">
          <div className="pb-eyebrow"><UsersIcon size={11} className="inline mr-1"/> Investoren mit Zugriff</div>
          <div className="text-2xl font-display font-bold text-navy mt-1 tabular-nums">{investorsWithAccess}</div>
          <div className="text-[10px] text-navy/55 mt-1 font-sans">{investorsSummary.length} gesamt im Pool</div>
        </div>
        <div className="pb-kpi">
          <div className="pb-eyebrow"><ActivityIcon size={11} className="inline mr-1"/> Events (7T)</div>
          <div className="text-2xl font-display font-bold text-navy mt-1 tabular-nums">{stats?.events_7d ?? "—"}</div>
          <div className="text-[10px] text-navy/55 mt-1 font-sans">Up- + Downloads + Views</div>
        </div>
        <div className="pb-kpi">
          <div className="pb-eyebrow"><Eye size={11} className="inline mr-1"/> Investor-Downloads (90T)</div>
          <div className="text-2xl font-display font-bold text-navy mt-1 tabular-nums">
            {stats?.top_downloaded_90d?.reduce((s, d) => s + (d.downloads_90d || 0), 0) || 0}
          </div>
          <div className="text-[10px] text-navy/55 mt-1 font-sans">
            Top-Datei: {stats?.top_downloaded_90d?.[0]?.downloads_90d || 0}×
          </div>
        </div>
      </section>

      {/* Tab-Switcher */}
      <section className="border-b border-navy/15 flex items-center gap-1" data-testid="dr-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`dr-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={
                "inline-flex items-center gap-1.5 px-4 py-2.5 text-xs uppercase tracking-widish font-sans font-medium border-b-2 -mb-px transition-colors " +
                (active
                  ? "border-gold text-navy bg-ledger/50"
                  : "border-transparent text-navy/55 hover:text-navy hover:border-navy/30")
              }
            >
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </section>

      {loading && (
        <div className="py-12 text-center text-navy/55 font-sans">Lade Daten…</div>
      )}

      {!loading && tab === "links" && (
        <MagicLinksTab
          items={magicLinks}
          onlyAktiv={onlyAktiv}
          setOnlyAktiv={setOnlyAktiv}
          onOpenInvestor={setDrawerId}
          onCopy={copyLink}
          onDeactivate={handleDeactivate}
        />
      )}
      {!loading && tab === "invs" && (
        <InvestorsTab items={investorsSummary} onOpenInvestor={setDrawerId} />
      )}
      {!loading && tab === "log" && (
        <AccessLogTab
          data={accessLog}
          filter={logFilter}
          setFilter={setLogFilter}
          stats={stats}
        />
      )}

      <InvestorDrawer
        investorId={drawerId}
        onClose={() => setDrawerId(null)}
        onChange={load}
        initialTab="datenraum"
      />
    </div>
  );
}

// ============================================================================
// Tab 1: Magic-Links
// ============================================================================
function MagicLinksTab({ items, onlyAktiv, setOnlyAktiv, onOpenInvestor, onCopy, onDeactivate }) {
  return (
    <section className="bg-white border border-navy/15" data-testid="dr-magic-links-section">
      <div className="border-b border-navy/10 px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm font-sans text-navy">
          <strong className="tabular-nums text-base">{items.length}</strong> Magic-Link{items.length === 1 ? "" : "s"}
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-sans text-navy/75 cursor-pointer">
          <input
            data-testid="dr-only-aktiv"
            type="checkbox"
            checked={onlyAktiv}
            onChange={(e) => setOnlyAktiv(e.target.checked)}
            className="accent-[#C4922A]"
          />
          Nur aktive Links
        </label>
      </div>

      {items.length === 0 ? (
        <div className="px-6 py-12 text-center text-navy/55 font-sans">
          Noch keine Magic-Links erstellt.<br/>
          <span className="text-[12px]">Erstelle einen Link im Investor-Drawer → Tab „Datenraum-Freigabe".</span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="pb-table" data-testid="dr-magic-links-table">
            <thead>
              <tr>
                <th>Investor</th>
                <th>Status</th>
                <th>Ablauf</th>
                <th className="text-right">Zugriffe</th>
                <th>Erstellt</th>
                <th className="text-right">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => {
                const inv = m.investor || {};
                return (
                  <tr key={m.token} data-testid={`dr-ml-row-${m.token.slice(0, 8)}`}>
                    <td className="max-w-sm">
                      <button
                        onClick={() => inv.id && onOpenInvestor(inv.id)}
                        className="text-navy hover:text-gold font-medium text-left"
                        data-testid={`dr-ml-investor-${m.token.slice(0, 8)}`}
                      >
                        {inv.firma || "—"}
                      </button>
                      {inv.betreuer_intern && (
                        <div className="text-[10px] uppercase tracking-widish text-navy/45 font-sans mt-0.5">
                          {inv.betreuer_intern} · {inv.phase || ""}
                        </div>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={m.status} />
                    </td>
                    <td className="font-sans text-xs text-navy/75 tabular-nums">
                      {m.ablauf_datum}
                      {m.abgelaufen && (
                        <div className="text-[10px] text-tot uppercase tracking-widish">abgelaufen</div>
                      )}
                    </td>
                    <td className="font-sans text-xs text-right tabular-nums text-navy/75">
                      <strong className="text-navy">{m.anzahl_zugriffe || 0}</strong>
                      {m.max_zugriffe ? <span className="text-navy/45"> / {m.max_zugriffe}</span> : ""}
                      {m.letzter_zugriff && (
                        <div className="text-[10px] text-navy/45">letzter: {fmtRel(m.letzter_zugriff)}</div>
                      )}
                    </td>
                    <td className="font-sans text-[11px] text-navy/65">
                      {fmtDate(m.erstellt_am)}
                      <div className="text-navy/45">{m.erstellt_von}</div>
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <button
                        onClick={() => onCopy(m.token)}
                        data-testid={`dr-ml-copy-${m.token.slice(0, 8)}`}
                        className="text-navy/65 hover:text-gold inline-flex items-center mr-3"
                        title="Magic-Link kopieren"
                      >
                        <Copy size={13} />
                      </button>
                      {m.status === "aktiv" && (
                        <button
                          onClick={() => onDeactivate(m.token, inv.firma)}
                          data-testid={`dr-ml-deactivate-${m.token.slice(0, 8)}`}
                          className="text-navy/65 hover:text-tot inline-flex items-center"
                          title="Deaktivieren"
                        >
                          <XCircle size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const StatusBadge = ({ status }) => {
  const map = {
    aktiv:        { color: "border-heiss/40 bg-heiss/10 text-heiss", label: "Aktiv" },
    deaktiviert:  { color: "border-navy/20 bg-ledger text-navy/55", label: "Deaktiviert" },
    abgelaufen:   { color: "border-tot/40 bg-tot/10 text-tot", label: "Abgelaufen" },
  };
  const cfg = map[status] || map.deaktiviert;
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widish border ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

// ============================================================================
// Tab 2: Investoren-Übersicht
// ============================================================================
function InvestorsTab({ items, onOpenInvestor }) {
  return (
    <section className="bg-white border border-navy/15" data-testid="dr-invs-section">
      <div className="border-b border-navy/10 px-5 py-3 text-sm font-sans text-navy">
        <strong className="tabular-nums text-base">{items.length}</strong> Investor{items.length === 1 ? "" : "en"} mit Datenraum-Bezug
      </div>
      {items.length === 0 ? (
        <div className="px-6 py-12 text-center text-navy/55 font-sans">
          Noch keine Investoren mit Datenraum-Zugriff oder Freigabe.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="pb-table" data-testid="dr-invs-table">
            <thead>
              <tr>
                <th>Investor</th>
                <th>Phase</th>
                <th className="text-right">Magic-Links</th>
                <th className="text-right">Freigaben</th>
                <th>Letzter Zugriff</th>
                <th className="text-right">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => (
                <tr key={inv.id} data-testid={`dr-inv-row-${inv.id}`}>
                  <td className="max-w-sm">
                    <button
                      onClick={() => onOpenInvestor(inv.id)}
                      className="text-navy hover:text-gold font-medium text-left"
                    >
                      {inv.firma}
                    </button>
                    {inv.betreuer_intern && (
                      <div className="text-[10px] uppercase tracking-widish text-navy/45 font-sans mt-0.5">
                        {inv.betreuer_intern}
                      </div>
                    )}
                  </td>
                  <td className="font-sans text-xs text-navy/75">{inv.phase || "—"}</td>
                  <td className="text-right tabular-nums">
                    <span className="text-navy font-medium">{inv.magic_links_aktiv}</span>
                    <span className="text-navy/45 text-xs"> / {inv.magic_links_total}</span>
                  </td>
                  <td className="text-right tabular-nums text-navy">{inv.manual_assignments}</td>
                  <td className="font-sans text-[11px] text-navy/65">
                    {inv.letzter_zugriff ? fmtRel(inv.letzter_zugriff) : <span className="text-navy/40">—</span>}
                  </td>
                  <td className="text-right">
                    <button
                      onClick={() => onOpenInvestor(inv.id)}
                      data-testid={`dr-inv-open-${inv.id}`}
                      className="text-xs uppercase tracking-widish text-navy hover:text-gold inline-flex items-center gap-1.5 font-sans"
                    >
                      Freigabe verwalten <ExternalLink size={11} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ============================================================================
// Tab 3: Zugriffs-Log
// ============================================================================
function AccessLogTab({ data, filter, setFilter, stats }) {
  return (
    <div className="space-y-4">
      {/* Top-Downloads */}
      {stats?.top_downloaded_90d?.length > 0 && (
        <section className="pb-card" data-testid="dr-top-downloads">
          <div className="pb-eyebrow mb-3"><Eye size={11} className="inline mr-1"/> Top-Downloads (90 Tage)</div>
          <ul className="space-y-1.5">
            {stats.top_downloaded_90d.map((d) => (
              <li key={d.file_id} className="text-sm font-sans flex justify-between items-center border-b border-navy/10 pb-1.5">
                <span className="text-navy truncate flex-1">{d.filename}</span>
                <span className="text-navy/55 text-[11px] mx-3">{d.kategorie}</span>
                <span className="text-gold font-medium tabular-nums">{d.downloads_90d}×</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="bg-white border border-navy/15" data-testid="dr-log-section">
        <div className="border-b border-navy/10 px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm font-sans text-navy">
            <strong className="tabular-nums text-base">{data.total}</strong> Events
          </div>
          <select
            data-testid="dr-log-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pb-input--full max-w-[180px] text-xs"
          >
            <option value="">Alle Akteure</option>
            <option value="team">Team</option>
            <option value="investor">Investoren</option>
          </select>
        </div>
        {data.items.length === 0 ? (
          <div className="px-6 py-12 text-center text-navy/55 font-sans">Noch keine Events.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="pb-table" data-testid="dr-log-table">
              <thead>
                <tr>
                  <th>Zeitpunkt</th>
                  <th>Akteur</th>
                  <th>Aktion</th>
                  <th>Datei / Investor</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((e) => (
                  <tr key={e.id} data-testid={`dr-log-row-${e.id}`}>
                    <td className="font-sans text-[11px] text-navy/75 tabular-nums whitespace-nowrap">
                      {fmtDate(e.timestamp)}
                      <div className="text-navy/45">{fmtRel(e.timestamp)}</div>
                    </td>
                    <td>
                      <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widish border ${
                        e.akteur_typ === "investor" ? "border-gold/40 bg-gold/10 text-gold" : "border-navy/20 bg-ledger text-navy/80"
                      }`}>
                        {e.akteur_typ === "investor" ? "Investor" : "Team"}
                      </span>
                      <div className="text-[11px] text-navy/65 font-sans mt-0.5">{e.akteur}</div>
                    </td>
                    <td className="font-sans text-xs">
                      <ActionBadge aktion={e.aktion} />
                    </td>
                    <td className="font-sans text-[11px] text-navy/65 max-w-sm break-words">
                      {e.file_id ? <code className="text-[10px]">{e.file_id.slice(0, 8)}…</code> : "—"}
                      {e.extra?.filename && (
                        <div className="text-navy text-xs">{e.extra.filename}</div>
                      )}
                    </td>
                    <td className="font-sans text-[10px] text-navy/45 font-mono">{e.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

const ActionBadge = ({ aktion }) => {
  const map = {
    upload:           "Upload",
    download:         "Download",
    view_list:        "Liste angesehen",
    invalid_token:    "Ungültiger Token",
    expired:          "Token abgelaufen",
    deactivated:      "Token deaktiviert",
    max_reached:      "Max-Zugriffe",
    forbidden:        "Verweigert",
    forbidden_intern: "Intern (verweigert)",
  };
  const danger = ["invalid_token", "expired", "deactivated", "max_reached", "forbidden", "forbidden_intern"].includes(aktion);
  return (
    <span className={
      "inline-block px-2 py-0.5 text-[10px] uppercase tracking-widish border " +
      (danger ? "border-tot/40 bg-tot/5 text-tot" : "border-navy/20 bg-ledger text-navy/80")
    }>
      {danger && <AlertTriangle size={9} className="inline mr-1" />}
      {map[aktion] || aktion}
    </span>
  );
};
